import { CalculatorOutlined, CalendarOutlined, CheckCircleOutlined, CheckOutlined, CloseCircleOutlined, CloseSquareOutlined, FileDoneOutlined, FolderOpenOutlined, HourglassOutlined, InfoCircleOutlined, LockOutlined, ProfileOutlined, ReloadOutlined, RollbackOutlined, SearchOutlined, StopOutlined } from "@ant-design/icons";
import { Typography as AntTypography, Card, Col, DatePicker, Grid, Modal, Row, Spin, Table, Tooltip, Typography } from "antd";
import appSettings from "apps/services/config";
import axios from "axios";
import dayjs from "dayjs";
import moment, { Moment } from "moment";
import React, { CSSProperties, useEffect, useMemo, useRef, useState } from "react";

const { RangePicker } = DatePicker;
const { Title } = Typography;
const { useBreakpoint } = Grid;

const aquaticColors = {
  enquiry: '#1976D2',
  open: '#64B5F6',
  assessment: '#E53935',
  approved: '#4CAF50',
  rejected: '#D32F2F',
  cost: '#FDD835',
  quotation: '#5C6BC0',
  renegotiated: '#FB8C00',
  accepted: '#00796B',
  pending: '#F9A825',
  cancelledPostAccept: '#6D4C41',
  closed: '#455A64',
  finalRejected: '#B71C1C',
  cancelled: '#8E24AA',
};


const defaultMockData = {
  acceptancePending: 0,
  assessmentApproved: 0,
  assessmentRejected: 0,
  cancelledAfterAcceptance: 0,
  reNegotiated: 0,
  underAssessment: 0,
  enquiries: 0,
  accepted: 0,
  rejections: 0,
  costSheets: 0,
  quotations: 0,
  cancelled: 0,
  closed: 0,
  open: 0,
  rejectionReasons: [],
};

const columns = [
  { title: "Rejection Reason", dataIndex: "reason", key: "reason" },
  { title: "No of rejections", dataIndex: "count", key: "count" },
];

const EnquiryDashboard = () => {
  const [dateRange, setDateRange] = useState<[Moment, Moment] | null>([
    moment().subtract(2, 'days'),
    moment().subtract(1, 'days')
  ]);
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(defaultMockData);
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const screens = useBreakpoint();
  const [hoveredCard, setHoveredCard] = useState<string>('');
  const { lg, xl } = useBreakpoint();
  const headerRef = useRef<HTMLDivElement>(null);
  const processApiData = (apiData) => {
    let processedData = {
      acceptancePending: 0,
      assessmentApproved: 0,
      assessmentRejected: 0,
      cancelledAfterAcceptance: 0,
      reNegotiated: 0,
      underAssessment: 0,
      enquiries: 0,
      accepted: 0,
      rejections: 0,
      costSheets: 0,
      quotations: 0,
      cancelled: 0,
      closed: 0,
      open: 0,
      rejectionReasons: [],
    };

    apiData.forEach((item) => {
        const count = parseInt(item.cnt) || 0;

        switch (item.status) {
            case "Total Enquiries":
                processedData.enquiries = count;
                break;
            case "ACCEPTED":
                processedData.accepted += count;
                break;
            case "OPEN":
                processedData.open += count;
                break;
            case "COSTING APPROVED":
                processedData.costSheets += count;
                break;
            case "QUOTATION SENT":
                processedData.quotations += count;
                break;
            case "CANCELLED":
                processedData.cancelled += count;
                break;
            case "CLOSED":
                processedData.closed += count;
                break;
            case "ACCEPTANCE PENDING":
                processedData.acceptancePending += count;
                break;
            case "ASSESMENT APPROVED":
                processedData.assessmentApproved += count;
                break;
            case "ASSESMENT REJECTED":
                processedData.assessmentRejected += count;
                break;
            case "CANCELLED AFTER ACCEPTANCE":
                processedData.cancelledAfterAcceptance += count;
                break;
            case "RENEGOTIATED":
                processedData.reNegotiated += count;
                break;
            case "UNDER ASSESMENT":
                processedData.underAssessment += count;
                break;
            case "REJECTED":
                processedData.rejections += count;
                processedData.rejectionReasons.push({ reason: "No reason provided", count });
                break;
        }

        if (item.Rejected) {
          item.Rejected.forEach(({ cnt, reason }) => {
            const rejectionCount = parseInt(cnt) || 0;
            processedData.rejections += rejectionCount;
            processedData.rejectionReasons.push({ 
              reason: reason || "No reason provided", 
              count: rejectionCount 
            });
          });
        }
    });

    return processedData;
  };

  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        const content = headerRef.current.parentElement;
        const card = content?.closest('.ant-card');
        
        if (card) {
          const cardRect = card.getBoundingClientRect();
          const headerRect = headerRef.current.getBoundingClientRect();
          
          if (cardRect.top < 0 && cardRect.bottom > headerRect.height) {
            // Make header fixed when card is partially scrolled
            headerRef.current.style.position = 'fixed';
            headerRef.current.style.top = '0';
            headerRef.current.style.zIndex = '1000';
            // ... other styling
          } else {
            // Return to normal when card is fully visible or scrolled out
            headerRef.current.style.position = 'relative';
            // ... reset styles
          }
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const fetchData = async (fromDate, toDate) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${appSettings.anlytics_url}`+`/mainDashboard/getEnquiresStatusWiseCount`,
        {
          fromDate,
          toDate
        }
      );

      if (response.data?.status) {
        const processedData = processApiData(response.data.data);
        setDashboardData(processedData);
        setRejectionReasons(processedData.rejectionReasons);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setDashboardData(defaultMockData);
      setRejectionReasons([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (dates, dateStrings) => {
    setDateRange(dates);
    if (dateStrings[0] && dateStrings[1]) {
      fetchData(dateStrings[0], dateStrings[1]);
    }
  };

  useEffect(() => {
    const defaultFromDate = dayjs().subtract(2, 'day').format('YYYY-MM-DD');
    const defaultToDate = dayjs().format('YYYY-MM-DD');
    fetchData(defaultFromDate, defaultToDate);
  }, []);

  const cardData = [
    { 
      title: "Total Enquiries", 
      value: dashboardData.enquiries, 
      color: aquaticColors.enquiry,
      icon: <ProfileOutlined style={{ fontSize: '20px' }} />
    },
    { 
      title: "Open", 
      value: dashboardData.open, 
      color: aquaticColors.open,
      icon: <FolderOpenOutlined style={{ fontSize: '20px' }} />
    },
    { 
      title: "Under Assessment", 
      value: dashboardData.underAssessment, 
      color: aquaticColors.assessment,
      icon: <SearchOutlined style={{ fontSize: '20px' }} />
    },
    { 
      title: "Assessment Approved", 
      value: dashboardData.assessmentApproved, 
      color: aquaticColors.approved,
      icon: <CheckCircleOutlined style={{ fontSize: '20px' }} />
    },
    { 
      title: "Assessment Rejected", 
      value: dashboardData.assessmentRejected, 
      color: aquaticColors.rejected,
      icon: <CloseCircleOutlined style={{ fontSize: '20px' }} />
    },
    { 
      title: "Cost Sheets", 
      value: dashboardData.costSheets, 
      color: aquaticColors.cost,
      icon: <CalculatorOutlined style={{ fontSize: '20px' }} />
    },
    { 
      title: "Quotations", 
      value: dashboardData.quotations, 
      color: aquaticColors.quotation,
      icon: <FileDoneOutlined style={{ fontSize: '20px' }} />
    },
    { 
      title: "Re-Negotiated", 
      value: dashboardData.reNegotiated, 
      color: aquaticColors.renegotiated,
      icon: <ReloadOutlined style={{ fontSize: '20px' }} />
    },
    { 
      title: "Accepted", 
      value: dashboardData.accepted, 
      color: aquaticColors.accepted,
      icon: <CheckOutlined style={{ fontSize: '20px' }} />
    },
    { 
      title: "Acceptance Pending", 
      value: dashboardData.acceptancePending, 
      color: aquaticColors.pending,
      icon: <HourglassOutlined style={{ fontSize: '20px' }} />
    },
    { 
      title: "Cancelled After Acceptance", 
      value: dashboardData.cancelledAfterAcceptance, 
      color: aquaticColors.cancelledPostAccept,
      icon: <RollbackOutlined style={{ fontSize: '20px' }} />
    },
    { 
      title: "Closed", 
      value: dashboardData.closed, 
      color: aquaticColors.closed,
      icon: <LockOutlined style={{ fontSize: '20px' }} />
    },
    { 
      title: "Rejections", 
      value: dashboardData.rejections, 
      color: aquaticColors.finalRejected,
      icon: <CloseSquareOutlined style={{ fontSize: '20px' }} />,
      onClick: true
    },
    { 
      title: "Cancelled", 
      value: dashboardData.cancelled, 
      color: aquaticColors.cancelled,
      icon: <StopOutlined style={{ fontSize: '20px' }} />
    }
  ].map(card => ({
    ...card,
    onClick: card.title === "Rejections"
  }));

  const responsiveCols = useMemo(() => ({
    xs: 24,
    sm: 12,
    md: 3,
    lg: 3.5,
    xl: 3.5,
    xxl: 3
  }), []);

  const handleCardInteraction = (title: string) => {
    if (title === "Rejections") {
      setIsModalVisible(true);
    }
  };

  const colors = {
    primary: '#2D3A4B',
    secondary: '#4A90E2',
    accent: '#FF6B6B',
    background: '#F8F9FA',
    textSecondary: '#6C757D',
    success: '#28A745',
    warning: '#FFC107',
    headerBg: '#4A90E2',
    headerText: '#FFFFFF'
  };

  const cardStyles = {
    metricCard: (color: string) => ({
      background: 'white',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      borderLeft: `4px solid ${color}`,
      transition: 'all 0.2s ease',
      margin: '4px',
      ':hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 16px rgba(0,0,0,0.12)'
      }
    } as CSSProperties),
    tableCard: {
      background: 'white',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      height: '100%'
    }
  };

  const conversionRate = dashboardData.enquiries > 0 ? ((dashboardData.accepted / dashboardData.enquiries) * 100).toFixed(2): "0.00";

  const headerLayout = useMemo(() => ({
    direction: screens.xs ? 'vertical' : 'horizontal',
    gutter: screens.xs ? [0, 16] as [number, number] : [16, 0] as [number, number]
  }), [screens]);


  return (
    <div style={{ 
      padding: screens.xs ? '12px' : '24px', 
      background: colors.background,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px'
    }}>
      <Spin spinning={loading}>
        <Row 
          justify="space-between" 
          align={screens.xs ? 'top' : 'middle'}
          style={{ marginBottom: '8px' }}
          gutter={headerLayout.gutter}
        >
          <Col>
            <Title 
              level={screens.xs ? 5 : 4} 
              style={{ margin: 0, color: colors.primary }}
            >
              Enquiry Analytics
              {/* <AntTypography.Text 
                type="secondary" 
                style={{ 
                  display: 'block', 
                  fontSize: screens.xs ? '12px' : '14px',
                  color: colors.textSecondary
                }}
              >
                Customer engagement metrics
              </AntTypography.Text> */}
            </Title>
          </Col>
          <Col style={{ 
            display: 'flex', 
            flexDirection: screens.xs ? 'column' : 'row', 
            alignItems: screens.xs ? 'stretch' : 'center',
            gap: 16,
            width: screens.xs ? '100%' : 'auto'
          }}>
            <div style={{ 
              background: `${colors.success}15`,
              padding: '8px 12px',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              order: screens.xs ? 2 : 1,
              width: screens.xs ? '100%' : 'auto'
            }}>
              <AntTypography.Text strong style={{ color: colors.success }}>
                Conversion Rate: {conversionRate}%
              </AntTypography.Text>
              <Tooltip title="(Accepted/Total)×100">
                <InfoCircleOutlined style={{ 
                  color: colors.textSecondary,
                  fontSize: 14,
                  cursor: 'help'
                }}/>
              </Tooltip>
              </div>
            <RangePicker
              onChange={handleDateChange}
              value={dateRange}
              style={{ 
                width: 280,
                borderRadius: '8px',
                borderColor: "#1890ff",
              }}
              suffixIcon={<CalendarOutlined style={{ color: "#1890ff" }} />}
              size="large"
            />
          </Col>
        </Row>

        <div style={{ 
          display: 'grid',
          gridTemplateColumns: lg || xl ? 'repeat(7, 1fr)' : 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '16px',
          width: '100%'
        }}>
          {cardData.map((card, index) => {
            const cardContent = (
              <div key={index} style={{ 
                height: '100%',
                minWidth: lg || xl ? 'auto' : '200px'
              }}
              onClick={() => card.onClick && handleCardInteraction(card.title)}
              >
                <Card
                  bordered={false}
                  style={{
                    ...cardStyles.metricCard(card.color),
                    height: '100%',
                    width: '100%',
                    transform: hoveredCard === card.title ? 'scale(1.02)' : 'none',
                    transition: 'transform 0.2s',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    padding: '12px'
                  }}
                  hoverable={card.onClick}
                  onMouseEnter={() => card.onClick && setHoveredCard(card.title)}
                  onMouseLeave={() => card.onClick && setHoveredCard('')}
                >
                  <div>
                    <AntTypography.Text 
                      strong 
                      style={{ 
                        color: colors.textSecondary,
                        fontSize: screens.xs ? 12 : 14,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        marginBottom: 8,
                        lineHeight: '16px',
                        fontWeight: 'bold',
                        whiteSpace: 'normal',
                        minHeight: '2.8em',
                      }}
                    >
                      {card.title}
                    </AntTypography.Text>
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end'
                  }}>
                    <div style={{
                      background: `${card.color}20`,
                      padding: 6,
                      borderRadius: 6,
                    }}>
                      {React.cloneElement(card.icon, { 
                        style: { 
                          fontSize: screens.xs ? 16 : 18,
                          color: card.color
                        } 
                      })}
                    </div>

                    <Title 
                      level={4} 
                      style={{ 
                        margin: 0,
                        color: card.color,
                        fontSize: screens.xs ? 16 : 18,
                        lineHeight: '24px',
                        fontWeight: 'bold'
                      }}
                    >
                      {card.value}
                    </Title>
                  </div>
                </Card>
              </div>
            );

            return card.title === "Rejections" ? (
              <Tooltip key={index} title="Click for rejections details" placement="top">
                {cardContent}
              </Tooltip>
            ) : (
              cardContent
            );
          })}
        </div>
        <Modal
          title="Rejection Analysis"
          visible={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={null}
          width={screens.xs ? "90%" : "60%"}
        >
          <Table
            dataSource={rejectionReasons}
            columns={columns}
            pagination={false}
            size="middle"
            scroll={{ y: 400 }}
            rowKey="reason"
            bordered
          />
        </Modal>
      </Spin>
  </div>
);
};

export default EnquiryDashboard;